import * as React from "react";

import { api } from "@repo/backend";
import { Badge } from "@repo/ui/components/badge";
import { Button } from "@repo/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui/components/card";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@repo/ui/components/empty";
import { ScrollArea } from "@repo/ui/components/scroll-area";
import { cn } from "@repo/ui/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useAction } from "convex/react";
import {
  ChevronLeft,
  ChevronRight,
  DownloadIcon,
  ImageIcon,
  LoaderCircle,
  RefreshCw,
  Trash2Icon,
} from "lucide-react";
import { toast } from "sonner";

import { featureFlags } from "@/config";
import { usePolling } from "@/hooks/use-polling";
import {
  deleteUploadDirect,
  listUploadsDirect,
  shouldUseDirectFanPicsWorker,
  type UploadItem,
  type UploadListData,
  type UploadSceneFacet,
} from "@/lib/fan-pics-client";
import { useRoleAccess } from "@/lib/role-access";

export const Route = createFileRoute("/_authed/fan-pics/uploads")({
  head: () => ({ meta: [{ title: "Uploads" }] }),
  component: RouteComponent,
});

const UPLOADS_POLL_MS = 20_000;

type UploadFilters = {
  sceneId: string;
};

function RouteComponent() {
  const { isAdmin, isPhotographer } = useRoleAccess();
  const isReadOnly = isPhotographer || !isAdmin;
  const confirm = useConfirmationDialog();
  const listUploads = useAction(api.fanPics.listUploads);
  const deleteUpload = useAction(api.fanPics.deleteUpload);
  const useDirectWorker = shouldUseDirectFanPicsWorker;
  const [filters, setFilters] = React.useState<UploadFilters>({ sceneId: "all" });
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState<string | null>(null);
  const [uploads, setUploads] = React.useState<UploadItem[]>([]);
  const [facets, setFacets] = React.useState<UploadListData["facets"]>({ scenes: [] });
  const [total, setTotal] = React.useState(0);
  const [selectedUploadId, setSelectedUploadId] = React.useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState<Date | null>(null);

  const selectedUpload = React.useMemo(
    () => uploads.find((upload) => upload.id === selectedUploadId) ?? uploads[0] ?? null,
    [selectedUploadId, uploads],
  );
  const selectedIndex = React.useMemo(
    () => uploads.findIndex((upload) => upload.id === selectedUpload?.id),
    [selectedUpload, uploads],
  );

  const loadUploads = React.useCallback(
    async (options?: { append?: boolean; page?: number; silent?: boolean }) => {
      if (options?.append) setIsLoadingMore(true);
      else if (!options?.silent) setIsLoading(true);

      try {
        const values = {
          page: options?.page ?? 1,
          sceneId: filters.sceneId === "all" ? undefined : filters.sceneId,
        };
        const result = useDirectWorker
          ? await listUploadsDirect(values)
          : await listUploads(values);

        if (!result.ok) {
          throw new Error(result.error);
        }

        setUploads((current) =>
          options?.append ? [...current, ...result.data.items] : result.data.items,
        );
        setFacets(result.data.facets ?? { scenes: [] });
        setTotal(result.data.total);
        setLastUpdatedAt(new Date());
        setSelectedUploadId((current) =>
          result.data.items.some((upload) => upload.id === current)
            ? current
            : (result.data.items[0]?.id ?? null),
        );
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not load uploads.");
      } finally {
        if (options?.append) setIsLoadingMore(false);
        else if (!options?.silent) setIsLoading(false);
      }
    },
    [filters.sceneId, listUploads, useDirectWorker],
  );

  const selectByOffset = React.useCallback(
    (offset: number) => {
      if (!uploads.length) return;

      const currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
      const nextIndex = (currentIndex + offset + uploads.length) % uploads.length;
      setSelectedUploadId(uploads[nextIndex]?.id ?? null);
    },
    [selectedIndex, uploads],
  );

  const removeUploadFromView = React.useCallback((upload: UploadItem) => {
    setUploads((current) => current.filter((item) => item.id !== upload.id));
    setTotal((current) => Math.max(0, current - 1));
    setFacets((current) => ({
      scenes: current.scenes.map((scene) =>
        scene.sceneId === upload.sceneId
          ? { ...scene, count: Math.max(0, scene.count - 1) }
          : scene,
      ),
    }));
    setSelectedUploadId((current) => (current === upload.id ? null : current));
  }, []);

  const handleDelete = React.useCallback(
    async (upload: UploadItem) => {
      if (isReadOnly) return;

      const allowed = await confirm({
        title: "Delete upload",
        description: `Permanently delete "${upload.originalFilename}".`,
        confirmText: "Delete",
      });

      if (!allowed) return;

      setIsDeleting(upload.id);

      try {
        const result = useDirectWorker
          ? await deleteUploadDirect(upload.id)
          : await deleteUpload({ uploadId: upload.id });

        if (!result.ok) {
          throw new Error(result.error);
        }

        removeUploadFromView(upload);
        toast.success("Upload deleted.");
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Could not delete upload.");
      } finally {
        setIsDeleting(null);
      }
    },
    [confirm, deleteUpload, isReadOnly, removeUploadFromView, useDirectWorker],
  );

  React.useEffect(() => {
    void loadUploads();
  }, [loadUploads]);

  usePolling(() => loadUploads({ silent: true }), {
    enabled: !isPreviewOpen,
    intervalMs: UPLOADS_POLL_MS,
  });

  React.useEffect(() => {
    const isEditable = (target: EventTarget | null) =>
      target instanceof HTMLElement &&
      (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditable(event.target)) return;

      if (!isPreviewOpen) {
        if ((event.key === " " || event.key === "Enter") && selectedUpload) {
          event.preventDefault();
          setIsPreviewOpen(true);
        }
        if (event.key === "ArrowLeft") selectByOffset(-1);
        if (event.key === "ArrowRight") selectByOffset(1);
        return;
      }

      if (event.key === "Escape") setIsPreviewOpen(false);
      if (event.key === "ArrowLeft") selectByOffset(-1);
      if (event.key === "ArrowRight") selectByOffset(1);
      if ((event.key === "Delete" || event.key === "Backspace") && selectedUpload) {
        event.preventDefault();
        void handleDelete(selectedUpload);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleDelete, isPreviewOpen, selectByOffset, selectedUpload]);

  const totalInFacets = React.useMemo(
    () => facets.scenes.reduce((count, scene) => count + scene.count, 0),
    [facets.scenes],
  );

  return (
    <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
      <div className="h-full min-h-0 lg:sticky lg:top-4">
        <Card className="flex h-full flex-col overflow-hidden lg:max-h-[calc(100dvh-11rem)]">
          <CardHeader>
            <CardTitle>Scenes</CardTitle>
            <CardDescription>Filter uploads by scene.</CardDescription>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
            <ScrollArea className="min-h-0 flex-1 pr-2">
              <div className="flex flex-col gap-2">
                <Button
                  className="justify-between"
                  onClick={() => setFilters({ ...filters, sceneId: "all" })}
                  variant={filters.sceneId === "all" ? "secondary" : "ghost"}
                >
                  All scenes
                  <Badge variant="outline">{totalInFacets}</Badge>
                </Button>

                {facets.scenes.map((scene) => (
                  <SceneFilterItem
                    key={scene.sceneId}
                    activeSceneId={filters.sceneId}
                    scene={scene}
                    onSelect={(sceneId) => setFilters({ ...filters, sceneId })}
                  />
                ))}
              </div>
            </ScrollArea>
            <div className="mt-auto flex items-center justify-between gap-2 border-t pt-3 text-xs text-muted-foreground">
              <span className="truncate">
                {lastUpdatedAt ? `Updated ${lastUpdatedAt.toLocaleTimeString()}` : "Not updated"}
              </span>
              <Button size="sm" variant="outline" onClick={() => void loadUploads()}>
                {isLoading ? <LoaderCircle className="animate-spin" /> : <RefreshCw />}
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex h-full min-h-0 flex-col overflow-hidden lg:max-h-[calc(100dvh-11rem)]">
        <ScrollArea className="min-h-0 flex-1">
          <div className="flex flex-col gap-4 pr-2">
            {isLoading ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div className="aspect-square rounded-xl border bg-muted/20" key={index} />
                ))}
              </div>
            ) : null}

            {!isLoading && uploads.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ImageIcon className="size-4" />
                  </EmptyMedia>
                  <EmptyTitle>No uploads found</EmptyTitle>
                  <EmptyDescription>Try another scene filter.</EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : null}

            {uploads.length > 0 ? (
              <>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
                  {uploads.map((upload) => (
                    <UploadGridItem
                      key={upload.id}
                      upload={upload}
                      selected={selectedUpload?.id === upload.id}
                      deleting={isDeleting === upload.id}
                      onOpen={() => {
                        setSelectedUploadId(upload.id);
                        setIsPreviewOpen(true);
                      }}
                      onSelect={() => setSelectedUploadId(upload.id)}
                      onDelete={() => void handleDelete(upload)}
                      readOnly={isReadOnly}
                    />
                  ))}
                </div>

                {uploads.length < total ? (
                  <div className="flex justify-center pb-1">
                    <Button
                      onClick={() =>
                        void loadUploads({
                          append: true,
                          page: Math.floor(uploads.length / 24) + 1,
                          silent: true,
                        })
                      }
                      variant="outline"
                      disabled={isLoadingMore}
                    >
                      {isLoadingMore ? "Loading more..." : "Load more"}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </ScrollArea>
      </div>

      {isPreviewOpen && selectedUpload ? (
        <div className="fixed inset-0 z-50 flex flex-col bg-background/95 p-4 backdrop-blur-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold">{selectedUpload.sceneName}</h2>
              <p className="truncate text-sm text-muted-foreground">
                {selectedUpload.originalFilename}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <KeyboardHint>← → Navigate</KeyboardHint>
              <KeyboardHint>Del Delete</KeyboardHint>
              <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>
                Close
              </Button>
            </div>
          </div>

          <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="relative flex min-h-0 items-center justify-center overflow-hidden rounded-xl border bg-muted/20">
              {featureFlags.fanPicsUploadDownloads ? (
                <DownloadUploadButton
                  upload={selectedUpload}
                  className="absolute top-3 right-3 z-10 shadow-sm"
                />
              ) : null}
              <img
                alt={selectedUpload.originalFilename}
                className="max-h-full max-w-full object-contain"
                src={selectedUpload.assetUrl}
              />
              {uploads.length > 1 ? (
                <>
                  <Button
                    className="absolute top-1/2 left-3 -translate-y-1/2"
                    size="icon-lg"
                    variant="secondary"
                    onClick={() => selectByOffset(-1)}
                  >
                    <ChevronLeft />
                  </Button>
                  <Button
                    className="absolute top-1/2 right-3 -translate-y-1/2"
                    size="icon-lg"
                    variant="secondary"
                    onClick={() => selectByOffset(1)}
                  >
                    <ChevronRight />
                  </Button>
                </>
              ) : null}
            </div>

            <Card className="min-h-0">
              <CardHeader>
                <CardTitle>Upload</CardTitle>
                <CardDescription>
                  {selectedIndex + 1} / {uploads.length} in this filtered view
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 text-sm">
                <MetaRow label="Scene ID" value={selectedUpload.sceneId} mono />
                <MetaRow label="Upload ID" value={selectedUpload.id} mono />
                <MetaRow label="Uploader" value={selectedUpload.uploaderId ?? "unknown"} mono />
                <MetaRow
                  label="Uploaded"
                  value={new Date(selectedUpload.uploadedAt * 1000).toLocaleString()}
                />
                <MetaRow label="Size" value={formatFileSize(selectedUpload.sizeBytes)} />
              </CardContent>
              <div className="p-6 pt-0">
                <Button
                  className="w-full"
                  disabled={isReadOnly || isDeleting === selectedUpload.id}
                  onClick={() => void handleDelete(selectedUpload)}
                  variant="destructive"
                >
                  <Trash2Icon />
                  Delete upload
                </Button>
              </div>
            </Card>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SceneFilterItem({
  activeSceneId,
  scene,
  onSelect,
}: {
  activeSceneId: string;
  scene: UploadSceneFacet;
  onSelect: (sceneId: string) => void;
}) {
  return (
    <Button
      className="h-auto justify-between gap-3 py-2 text-left"
      onClick={() => onSelect(scene.sceneId)}
      variant={activeSceneId === scene.sceneId ? "secondary" : "ghost"}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate">{scene.sceneName}</span>
        <span className="block truncate text-xs text-muted-foreground">{scene.sceneId}</span>
      </span>
      <Badge variant="outline">{scene.count}</Badge>
    </Button>
  );
}

function UploadGridItem({
  upload,
  selected,
  deleting,
  onDelete,
  onOpen,
  onSelect,
  readOnly,
}: {
  upload: UploadItem;
  selected: boolean;
  deleting: boolean;
  onDelete: () => void;
  onOpen: () => void;
  onSelect: () => void;
  readOnly: boolean;
}) {
  return (
    <div
      className={cn(
        "group overflow-hidden rounded-xl border bg-card transition-colors hover:border-foreground/30",
        selected && "border-foreground ring-2 ring-ring/40",
      )}
    >
      <div className="relative aspect-square overflow-hidden bg-muted/20">
        <button
          type="button"
          className="block h-full w-full cursor-pointer text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          onClick={onSelect}
          onDoubleClick={onOpen}
        >
          <img
            alt={upload.originalFilename}
            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
            loading="lazy"
            src={upload.assetUrl}
          />
        </button>
        {featureFlags.fanPicsUploadDownloads ? (
          <DownloadUploadButton upload={upload} className="absolute top-2 right-2 shadow-sm" />
        ) : null}
      </div>
      <div className="grid gap-2 p-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{upload.sceneName}</p>
          <p className="truncate text-xs text-muted-foreground">{upload.originalFilename}</p>
        </div>
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{new Date(upload.uploadedAt * 1000).toLocaleDateString()}</span>
          <span>{formatFileSize(upload.sizeBytes)}</span>
        </div>
        <Button size="sm" variant="destructive" onClick={onDelete} disabled={readOnly || deleting}>
          <Trash2Icon />
          Delete
        </Button>
      </div>
    </div>
  );
}

const localAssetUrl = (assetUrl: string) => {
  const url = new URL(assetUrl, window.location.href);
  if (url.hostname === "127.0.0.1" && window.location.hostname === "localhost") {
    url.hostname = "localhost";
  }
  return url.toString();
};

async function downloadUpload(upload: UploadItem) {
  const response = await fetch(localAssetUrl(upload.assetUrl));

  if (!response.ok) {
    throw new Error("Could not download photo.");
  }

  const objectUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = upload.originalFilename || `${upload.id}.jpg`;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
}

function DownloadUploadButton({ className, upload }: { className?: string; upload: UploadItem }) {
  return (
    <Button
      aria-label={`Download ${upload.originalFilename}`}
      className={className}
      size="icon-sm"
      type="button"
      variant="secondary"
      onClick={(event) => {
        event.stopPropagation();
        void downloadUpload(upload).catch((error) => {
          toast.error(error instanceof Error ? error.message : "Could not download photo.");
        });
      }}
    >
      <DownloadIcon />
    </Button>
  );
}

function KeyboardHint({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-md border bg-muted/30 px-2 py-1 text-xs text-muted-foreground">
      {children}
    </span>
  );
}

function MetaRow({ label, mono, value }: { label: string; mono?: boolean; value: string }) {
  return (
    <div>
      <span className="text-muted-foreground">{label}</span>
      <p className={cn("break-all", mono && "font-mono text-xs")}>{value}</p>
    </div>
  );
}

function formatFileSize(sizeBytes: number) {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}
